/**
 * Is a chart bucket still being written?
 *
 * Every monthly series in Atlas is generated server-side as
 * `generate_series(0, N)` months back from
 * `date_trunc('month', now() at time zone 'Asia/Dubai')`, so the LAST bucket is
 * always the current, incomplete month. Measured on prod 2026-08-06, the
 * marketplace booking-volume series read 2026-05 = 8,309 · 2026-06 = 7,550 ·
 * 2026-07 = 7,516 · 2026-08 = 1,431 — six of August's 31 days plotted flush
 * against twelve full months. It reads as a collapse in business volume; it is
 * a calendar artifact.
 *
 * Deleting the bucket would be its own lie ("where's August?"), so the rule is:
 * keep it, mark it, and never let it into a delta.
 *
 * ONE definition. Three surfaces plot these buckets (marketplace fleet hub,
 * plus the shared booking-volume widget mounted by the Brand and Area 360s);
 * hand-rolling the calendar maths per surface is exactly how the metric
 * families in `.claude/docs/data-truth-failure-modes.md` §4 drifted apart.
 */

export type BucketGranularity = 'daily' | 'monthly';

export interface BucketCompleteness {
  granularity: BucketGranularity;
  /** Units elapsed in the bucket, counting the one currently in progress. */
  elapsed: number;
  /** Units the bucket will hold once it closes. */
  total: number;
  /** Days for a monthly bucket, hours for a daily one. */
  unit: 'day' | 'hour';
  /** True while the bucket is still accumulating, or has not started yet. */
  isPartial: boolean;
}

// Asia/Dubai (UTC+4, no DST) is the calendar the bucket-generating RPCs
// truncate against. Reading "now" from the browser's own zone would put a
// viewer west of GST on the previous Dubai day for part of every day, and the
// server-drawn bucket and the client-drawn label would disagree — the
// browser-side-date-math-against-server-state failure mode (§3).
const DUBAI_NOW = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
});

// Month name for the caption. UTC so the label can't shift a month at the
// boundary — the y/m are already resolved to Dubai by the time we get here.
const MONTH_LABEL = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
});

const DAY_LABEL = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function dubaiNow(now: Date): { year: number; month: number; day: number; hour: number } {
  const parts = DUBAI_NOW.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { year: part('year'), month: part('month'), day: part('day'), hour: part('hour') };
}

/** Day 0 of the next month is the last day of this one. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Elapsed/total for a bucket, or `null` when the bucket string doesn't parse
 * as the stated granularity.
 *
 * `null` rather than a guess: an unrecognised bucket is unknown, and unknown
 * must not render as a confident claim in either direction.
 */
export function bucketCompleteness(
  bucketIso: string,
  granularity: BucketGranularity,
  now: Date = new Date(),
): BucketCompleteness | null {
  const today = dubaiNow(now);

  if (granularity === 'monthly') {
    const m = /^(\d{4})-(\d{2})$/.exec(bucketIso);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;

    const total = daysInMonth(year, month);
    const bucketOrdinal = year * 12 + month;
    const nowOrdinal = today.year * 12 + today.month;
    if (bucketOrdinal < nowOrdinal) {
      return { granularity, elapsed: total, total, unit: 'day', isPartial: false };
    }
    // The current month is partial even on its last day — that day is itself
    // still being written, so `elapsed === total` is not the same as closed.
    const elapsed = bucketOrdinal === nowOrdinal ? today.day : 0;
    return { granularity, elapsed, total, unit: 'day', isPartial: true };
  }

  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bucketIso);
  if (!d) return null;
  const todayIso = `${String(today.year).padStart(4, '0')}-${String(today.month).padStart(2, '0')}-${String(today.day).padStart(2, '0')}`;
  // YYYY-MM-DD is lexicographically ordered, so string compare is date compare.
  if (bucketIso < todayIso) {
    return { granularity, elapsed: 24, total: 24, unit: 'hour', isPartial: false };
  }
  const elapsed = bucketIso === todayIso ? today.hour + 1 : 0;
  return { granularity, elapsed, total: 24, unit: 'hour', isPartial: true };
}

/** True only when we can prove the bucket is still accumulating. */
export function isPartialBucket(
  bucketIso: string,
  granularity: BucketGranularity,
  now: Date = new Date(),
): boolean {
  return bucketCompleteness(bucketIso, granularity, now)?.isPartial ?? false;
}

/**
 * Caption for a partial bucket, e.g. "August 2026 is incomplete — 6 of 31 days
 * elapsed." Returns `null` for a complete or unparseable bucket so callers
 * render nothing rather than an empty claim.
 */
export function partialBucketNote(
  bucketIso: string,
  granularity: BucketGranularity,
  now: Date = new Date(),
): string | null {
  const c = bucketCompleteness(bucketIso, granularity, now);
  if (!c || !c.isPartial) return null;

  const label =
    granularity === 'monthly'
      ? MONTH_LABEL.format(Date.UTC(Number(bucketIso.slice(0, 4)), Number(bucketIso.slice(5, 7)) - 1, 1))
      : DAY_LABEL.format(
          Date.UTC(
            Number(bucketIso.slice(0, 4)),
            Number(bucketIso.slice(5, 7)) - 1,
            Number(bucketIso.slice(8, 10)),
          ),
        );

  const units = `${c.elapsed} of ${c.total} ${c.unit}${c.total === 1 ? '' : 's'}`;
  return `${label} is incomplete — ${units} elapsed (Asia/Dubai). Not comparable with the buckets before it.`;
}
